import clsx from "clsx";
import { ComponentPropsWithoutRef, FC } from "react";
import { twMerge } from "tailwind-merge";
import { BUTTON_STYLE } from "./Button";
import Typography from "./Typography";

type CheckboxInputProps = {
  checked: boolean;
  onValueChange: (checked: boolean) => void;
  label?: string;
  size?: "s" | "m" | "l";
  disabled?: boolean;
} & ComponentPropsWithoutRef<"label">;

const checkboxSize: Record<
  "s" | "m" | "l",
  {
    track: string;
    padding: string;
  }
> = {
  s: {
    track: clsx("h-3.5 w-full"),
    padding: "p-0.5",
  },
  m: {
    track: clsx("h-4 w-full"),
    padding: "p-0.5",
  },
  l: {
    track: clsx("h-5 w-full"),
    padding: "p-0.5",
  },
};

const containerSize: Record<
  "s" | "m" | "l",
  {
    container: string;
  }
> = {
  s: {
    container: twMerge(
      BUTTON_STYLE.s.height,
      BUTTON_STYLE.s.width,
      "p-[2.5px]"
    ),
  },
  m: {
    container: twMerge(BUTTON_STYLE.m.height, BUTTON_STYLE.m.width, "p-1"),
  },
  l: {
    container: twMerge(BUTTON_STYLE.l.height, BUTTON_STYLE.l.width, "p-1.5"),
  },
};

const CheckboxInput: FC<CheckboxInputProps> = ({
  checked,
  onValueChange,
  label,
  size = "m",
  disabled = false,
  ...rest
}) => {
  const sizes = checkboxSize[size];

  return (
    <label
      {...rest}
      className={twMerge(
        `relative inline-flex items-center cursor-pointer gap-2 text-foreground-muted ${
          rest.className
        } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`
      )}
    >
      {label && <Typography variant="caption">{label}</Typography>}
      <div
        className={twMerge(containerSize[size].container, "flex items-center")}
      >
        <div className="relative w-full">
          <input
            disabled={disabled}
            checked={checked}
            onChange={() => onValueChange(!checked)}
            type="checkbox"
            value=""
            className="sr-only peer"
          />
          <div
            className={`${sizes.track} ${sizes.padding} bg-surface-tertiary rounded-full peer peer-checked:bg-primary flex items-center`}
          >
            <div
              className={`h-full aspect-square bg-white rounded-full ${
                checked ? "ml-auto" : ""
              }`}
            />
          </div>
        </div>
      </div>
    </label>
  );
};

export default CheckboxInput;
